import { useQuery } from '@tanstack/react-query';
import { billingApi } from '../lib/api';

// Feature keys that map to plan features
export type FeatureKey =
  | 'standard_reports_basic'
  | 'standard_reports_full'
  | 'basic_charts'
  | 'all_charts'
  | 'traffic_sources'
  | 'landing_exit_pages'
  | 'ecommerce_reports'
  | 'realtime_analytics'
  | 'hourly_heatmap'
  | 'period_comparison'
  | 'geographic_data'
  | 'device_analytics'
  | 'browser_os_analytics'
  | 'utm_tracking'
  | 'custom_date_range'
  | 'pdf_export'
  | 'help_docs'
  | 'email_support';

export function useFeatures() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage'],
    queryFn: async () => {
      const response = await billingApi.getUsage();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const hasFeature = (feature: FeatureKey): boolean => {
    if (!data?.features) return false;
    return data.features.includes(feature);
  };

  const features = data?.features || [];

  return {
    features,
    hasFeature,
    isLoading,
    error,
    // Convenience methods for common feature checks
    canAccessEcommerce: hasFeature('ecommerce_reports'),
    canAccessRealtime: hasFeature('realtime_analytics'),
    canAccessTrafficSources: hasFeature('traffic_sources'),
    canAccessHourlyHeatmap: hasFeature('hourly_heatmap'),
    canAccessLandingExit: hasFeature('landing_exit_pages'),
    canAccessGeographic: hasFeature('geographic_data'),
    canAccessDeviceAnalytics: hasFeature('device_analytics'),
    canAccessBrowserOS: hasFeature('browser_os_analytics'),
    canAccessPdfExport: hasFeature('pdf_export'),
    canAccessCustomDateRange: hasFeature('custom_date_range'),
    canAccessPeriodComparison: hasFeature('period_comparison'),
    canAccessAllCharts: hasFeature('all_charts'),
    canAccessUtmTracking: hasFeature('utm_tracking'),
  };
}
